import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const Footer: React.FC = () => {
  const location = useLocation();

  // Hide footer on dashboard pages
  const isDashboardPage = location.pathname === '/dashboard';
  if (isDashboardPage) {
    return null;
  }

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center space-x-3 mb-4">
              <span className="text-2xl font-bold text-teal-400">QuickReach</span>
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-2 py-1 rounded text-xs font-semibold">
                Private AI
              </span>
            </Link>
            <p className="text-gray-400 text-sm mb-4 max-w-md">
              Turn AI curiosity into business results with private, owned AI solutions that learn from your data and grow with your business.
            </p>
            
            <div className="flex space-x-4">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                <span className="sr-only">Twitter</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* AI Products */}
          <div>
            <h3 className="text-lg font-semibold mb-4">AI Solutions</h3>
            <ul className="space-y-2">
              <li><Link to="/db-talker" className="text-gray-400 hover:text-white transition-colors">DB Talker</Link></li>
              <li><Link to="/docflow-ai" className="text-gray-400 hover:text-white transition-colors">DocFlow AI</Link></li>
              <li><Link to="/visionedge-ai" className="text-gray-400 hover:text-white transition-colors">VisionEdge AI</Link></li>
              <li className="pt-2 border-t border-gray-700 mt-2"><span className="text-gray-500 text-sm font-semibold">Blockchain</span></li>
              <li><Link to="/blockchain-wallet" className="text-gray-400 hover:text-white transition-colors">Blockchain Wallet</Link></li>
              <li className="pt-2 border-t border-gray-700 mt-2"><span className="text-gray-500 text-sm font-semibold">IoT</span></li>
              <li><Link to="/edgesphere" className="text-gray-400 hover:text-white transition-colors">QuickReach EdgeSphere</Link></li>
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Solutions</h3>
            <ul className="space-y-2">
              <li><Link to="/models-by-industry" className="text-gray-400 hover:text-white transition-colors">Models by Industry</Link></li>
              <li><Link to="/roi-calculator" className="text-gray-400 hover:text-white transition-colors">ROI Calculator</Link></li>
              <li><Link to="/architecture" className="text-gray-400 hover:text-white transition-colors">Platform Architecture</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Resources</h3>
            <ul className="space-y-2">
              <li><Link to="/faq" className="text-gray-400 hover:text-white transition-colors">FAQ</Link></li>
              <li><a href="/#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</a></li>
              <li><Link to="/schedule-meeting" className="text-gray-400 hover:text-white transition-colors">Book a Meeting</Link></li>
              <li><Link to="/ai-evolution" className="text-gray-400 hover:text-white transition-colors">AI Evolution</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            &copy; 2025 QuickReach Private AI. Powered by Xamun AI + BlastAsia.
          </p>
          <div className="flex flex-wrap gap-6 mt-4 md:mt-0">
            <Link to="/schedule-meeting" className="text-gray-400 hover:text-white transition-colors text-sm">
              Contact Sales
            </Link>
            <Link to="/schedule-meeting" className="text-gray-400 hover:text-white transition-colors text-sm">
              Book Demo
            </Link>
            <Link to="/license" className="text-gray-400 hover:text-white transition-colors text-sm">
              License
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 